import React, { useCallback, useEffect, useRef, useState } from 'react';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const MARKET_URL = 'https://tapeout.market/circuits/tapeout';

function formatTime(ms) {
  if (!Number.isFinite(ms) || !ms) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function eventTypeLabel(type) {
  if (type === 'cold_start') return '冷启动';
  if (type === 'low_nand') return '命中提醒';
  if (type === 'new_listing') return '新挂单';
  if (type === 'probe') return '链路验证';
  if (type === 'test') return '测试';
  return type || '-';
}

function MinerList({ title, rows }) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>{title}（{list.length}）</h3>
      {list.length === 0 ? (
        <div className="volume-spike-empty">当前没有落在这一档的矿机。</div>
      ) : (
        <div className="bem-miner-table-wrap">
          <table className="bem-miner-table">
            <thead>
              <tr>
                <th>编号</th>
                <th>相对 NAND</th>
                <th>预计日产出</th>
                <th>售价</th>
                <th>可进入验证池</th>
                <th>当前矿池</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.listingKey || row.tokenId}>
                  <td>
                    <a href={row.url || `${MARKET_URL}/${row.tokenId}`} target="_blank" rel="noreferrer">
                      Circuit #{row.tokenId}
                    </a>
                  </td>
                  <td>{row.nandPct != null ? `${row.nandPct}%` : '-'}</td>
                  <td>{row.dailyBem && row.dailyBem !== '-' ? `≈ ${row.dailyBem} BEM / 天` : '-'}</td>
                  <td>{row.priceBnb ? `${row.priceBnb} BNB` : '-'}</td>
                  <td>{row.canEnterPool || '-'}</td>
                  <td>{row.currentPool || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TapeOutNandPage() {
  const [cloudStatus, setCloudStatus] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudMessage, setCloudMessage] = useState('');
  const [cloudBusy, setCloudBusy] = useState('');
  const [nandThresholdPct, setNandThresholdPct] = useState(90);
  const [settingsReady, setSettingsReady] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState('');

  const settingsSaveTimerRef = useRef(null);
  const settingsHydratedRef = useRef(false);
  const applyingRemoteRef = useRef(false);
  const settingsRef = useRef({ nandThresholdPct });
  const loadCloudStatusRef = useRef(null);

  useEffect(() => {
    settingsRef.current = { nandThresholdPct };
  }, [nandThresholdPct]);

  const applySettingsFromCloud = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    applyingRemoteRef.current = true;
    if (data.nandThresholdPct != null && Number.isFinite(Number(data.nandThresholdPct))) {
      setNandThresholdPct(Math.max(10, Math.min(500, Number(data.nandThresholdPct))));
    }
    window.setTimeout(() => {
      applyingRemoteRef.current = false;
    }, 0);
  }, []);

  const saveSettingsToCloud = useCallback(async (partial = {}, { quiet = false } = {}) => {
    const next = {
      nandThresholdPct: partial.nandThresholdPct ?? settingsRef.current.nandThresholdPct,
      ...partial,
    };
    if (!quiet) setCloudBusy('settings');
    try {
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tapeoutNand: next }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `保存失败 ${resp.status}`);
      setSettingsDirty(false);
      if (!quiet) setCloudMessage('配置已保存');
      await loadCloudStatusRef.current?.({ skipApplySettings: true, keepMessage: true });
      return true;
    } catch (err) {
      if (!quiet) setCloudMessage(err?.message || '配置保存失败');
      return false;
    } finally {
      if (!quiet) setCloudBusy('');
    }
  }, []);

  const loadCloudStatus = useCallback(async (opts = {}) => {
    setCloudLoading(true);
    try {
      const resp = await fetch(api('/api/tapeout-nand/status'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `状态读取失败 ${resp.status}`);
      const data = body.data || null;
      setCloudStatus(data);
      if (data && !opts.skipApplySettings) {
        applySettingsFromCloud(data);
        settingsHydratedRef.current = true;
        setSettingsReady(true);
      }
      if (!opts.keepMessage) setCloudMessage('');
    } catch (err) {
      setCloudMessage(err?.message || '状态读取失败');
      if (!settingsHydratedRef.current) {
        settingsHydratedRef.current = true;
        setSettingsReady(true);
      }
    } finally {
      setCloudLoading(false);
    }
  }, [applySettingsFromCloud]);

  loadCloudStatusRef.current = loadCloudStatus;

  useEffect(() => {
    loadCloudStatus();
    const timer = window.setInterval(() => loadCloudStatus({ skipApplySettings: true, keepMessage: true }), 15000);
    return () => window.clearInterval(timer);
  }, [loadCloudStatus]);

  useEffect(() => {
    if (!settingsHydratedRef.current || !settingsReady || applyingRemoteRef.current) return undefined;
    setSettingsDirty(true);
    if (settingsSaveTimerRef.current) window.clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = window.setTimeout(() => {
      if (applyingRemoteRef.current) return;
      saveSettingsToCloud({}, { quiet: false });
    }, 800);
    return () => {
      if (settingsSaveTimerRef.current) window.clearTimeout(settingsSaveTimerRef.current);
    };
  }, [nandThresholdPct, settingsReady, saveSettingsToCloud]);

  const saveCloudEnabled = useCallback(async (enabled) => {
    setCloudBusy('save');
    try {
      const payload = enabled
        ? { enabled: true }
        : { enabled: false };
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tapeoutNand: payload }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `保存失败 ${resp.status}`);
      setCloudMessage(
        enabled
          ? '监控已开启：第一次会冷启动记下当前已命中的挂单（不推送），之后每分钟扫全市场，新命中才推企业微信。'
          : '监控已关闭',
      );
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '开关保存失败');
    } finally {
      setCloudBusy('');
    }
  }, [loadCloudStatus]);

  const resetColdStart = useCallback(async () => {
    setCloudBusy('reset');
    try {
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tapeoutNand: { coldStarted: false, seenListingKeys: [] } }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `重置失败 ${resp.status}`);
      setCloudMessage('已清空基线。下一轮会重新冷启动，当前已命中的挂单不会立刻推送。');
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '重置失败');
    } finally {
      setCloudBusy('');
    }
  }, [loadCloudStatus]);

  const runCloudAction = useCallback(async (mode) => {
    const qs = mode === 'dry'
      ? '?dry=1&force=1'
      : mode === 'test'
        ? '?test=1'
        : mode === 'probe'
          ? '?probe=1'
          : '?force=1';
    setCloudBusy(mode);
    try {
      const resp = await fetch(api(`/api/cron/tapeout-nand${qs}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `请求失败 ${resp.status}`);
      const data = body.data || {};
      setCloudMessage(data.reason || (mode === 'test' ? '测试已完成' : '检查完成'));
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '动作失败');
    } finally {
      setCloudBusy('');
    }
  }, [loadCloudStatus]);

  const runMarketScan = useCallback(async () => {
    setCloudBusy('scan');
    setScanError('');
    try {
      const resp = await fetch(api('/api/tapeout-nand/scan'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `扫描失败 ${resp.status}`);
      setScanResult(body.data || null);
      const counts = body.data?.counts || {};
      setCloudMessage(`已扫描 ${body.data?.listingCount ?? 0} 条挂单：<90% ${counts.lt90 ?? 0}，<95% ${counts.lt95 ?? 0}`);
    } catch (err) {
      setScanError(err?.message || '扫描失败');
      setCloudMessage(err?.message || '扫描失败');
    } finally {
      setCloudBusy('');
    }
  }, []);

  const events = Array.isArray(cloudStatus?.recentEvents) ? cloudStatus.recentEvents : [];
  const quietLeft = cloudStatus?.quietUntil
    ? Math.max(0, Math.round((cloudStatus.quietUntil - Date.now()) / 1000))
    : 0;

  return (
    <div className="volume-spike-page primary-arb-page">
      <section className="cxmt-hero">
        <div>
          <span className="cxmt-eyebrow">TapeOut · BEM</span>
          <h1>BEM挖矿</h1>
          <p>
            不爬整页，只打官方 JSON。Cloudflare 每分钟扫描全市场挂单。
            同时满足：相对 NAND 低于阈值、可进入验证池为「是」、当前矿池为「验证池」，才推企业微信。
            机器人复用「持仓查询 → 机器人配置」，不会另开一个 Webhook。
          </p>
        </div>
        <div className="cxmt-hero-actions">
          <a href={MARKET_URL} target="_blank" rel="noreferrer">打开矿机市场</a>
          <button type="button" disabled={!!cloudBusy || cloudLoading} onClick={() => loadCloudStatus()}>
            刷新状态
          </button>
        </div>
      </section>

      <section className="volume-spike-panel volume-spike-cloud">
        <div className="volume-spike-panel-head">
          <div>
            <h2>全市场相对 NAND</h2>
            <p>
              一键读取当前全部挂单（官方 JSON，不分页点网页）。
              「小于 95%」包含「小于 90%」。区间按页面展示值统计：95%≤x&lt;120%、120%≤x&lt;150%、≥150%。
            </p>
          </div>
          <button type="button" disabled={!!cloudBusy} onClick={runMarketScan}>
            {cloudBusy === 'scan' ? '读取中…' : '一键读取全部挂单'}
          </button>
        </div>
        {scanResult ? (
          <>
            <div className="volume-spike-cloud-grid bem-scan-grid">
              <div className="is-hot">
                <span>小于 90%</span>
                <strong>{scanResult.counts?.lt90 ?? 0}</strong>
                <small>低 NAND，单独列表如下</small>
              </div>
              <div className="is-hot">
                <span>小于 95%</span>
                <strong>{scanResult.counts?.lt95 ?? 0}</strong>
                <small>含小于 90% 的矿机</small>
              </div>
              <div>
                <span>95% 到 120%</span>
                <strong>{scanResult.counts?.from95to120 ?? 0}</strong>
                <small>95% ≤ 相对 NAND &lt; 120%</small>
              </div>
              <div>
                <span>120% 到 150%</span>
                <strong>{scanResult.counts?.from120to150 ?? 0}</strong>
                <small>120% ≤ 相对 NAND &lt; 150%</small>
              </div>
              <div>
                <span>150% 以上</span>
                <strong>{scanResult.counts?.from150 ?? 0}</strong>
                <small>相对 NAND ≥ 150%</small>
              </div>
            </div>
            <p className="volume-spike-empty" style={{ marginTop: 10 }}>
              共 {scanResult.listingCount} 条挂单
              {scanResult.unknownCount ? `，其中 ${scanResult.unknownCount} 条算不出相对 NAND` : ''}
              {scanResult.elapsedMs ? ` · 耗时 ${(scanResult.elapsedMs / 1000).toFixed(1)} 秒` : ''}
              {scanResult.scannedAt ? ` · ${formatTime(scanResult.scannedAt)}` : ''}
            </p>
            <MinerList title="小于 90% 的矿机" rows={scanResult.lt90} />
            <MinerList title="小于 95% 的矿机" rows={scanResult.lt95} />
          </>
        ) : (
          <div className="volume-spike-empty">
            {scanError || '尚未读取。点右上角「一键读取全部挂单」，大约几秒到十几秒。'}
          </div>
        )}
      </section>

      <section className="volume-spike-panel volume-spike-cloud">
        <div className="volume-spike-panel-head">
          <div>
            <h2>监控开关</h2>
            <p>
              Webhook 复用「持仓查询 → 机器人配置」。云端每分钟 Cron 扫描全市场；命中才推企业微信，不另开机器人。
            </p>
          </div>
          <span className={`volume-spike-badge tone-${cloudStatus?.enabled && cloudStatus?.hasWeCom ? 'ok' : 'warm'}`}>
            {cloudLoading ? '读取中' : (cloudStatus?.enabled ? '监控中' : '未开启')}
          </span>
        </div>

        <div className="volume-spike-cloud-grid">
          <div>
            <span>企业微信</span>
            <strong>{cloudStatus?.hasWeCom ? '已配置' : '未配置'}</strong>
            <small>{cloudStatus?.hasWeCom ? '将复用站点机器人 Webhook' : '请先到持仓查询页同步 Webhook'}</small>
          </div>
          <div>
            <span>冷启动</span>
            <strong>{cloudStatus?.coldStarted ? '已完成' : '待建基线'}</strong>
            <small>已记录 {cloudStatus?.seenCount ?? 0} 条已提醒/已忽略命中</small>
          </div>
          <div>
            <span>当前节奏</span>
            <strong>每分钟 / 轮</strong>
            <small>
              {cloudStatus?.consecutiveErrors
                ? `连续失败 ${cloudStatus.consecutiveErrors} 次，正在退避`
                : quietLeft > 0
                  ? `静默 ${quietLeft}s 后下一轮`
                  : '云端每分钟扫描一次全市场'}
            </small>
          </div>
          <div>
            <span>最近检查</span>
            <strong>{cloudStatus?.lastCheckAt ? formatTime(cloudStatus.lastCheckAt) : '-'}</strong>
            <small>最近报警 {cloudStatus?.lastAlertAt ? formatTime(cloudStatus.lastAlertAt) : '暂无'}</small>
          </div>
        </div>

        <div className="volume-spike-cloud-actions">
          <button type="button" disabled={!!cloudBusy} onClick={() => saveCloudEnabled(!(cloudStatus?.enabled))}>
            {cloudBusy === 'save' ? '保存中…' : (cloudStatus?.enabled ? '关闭监控' : '开启监控')}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('run')}>
            {cloudBusy === 'run' ? '检查中…' : '立即轮询一次'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('dry')}>
            {cloudBusy === 'dry' ? '干跑中…' : '干跑（不推送）'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('test')}>
            {cloudBusy === 'test' ? '发送中…' : '发送企微测试文案'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('probe')}>
            {cloudBusy === 'probe' ? '推送中…' : '用 newest 一条验证并推企微'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={resetColdStart}>
            {cloudBusy === 'reset' ? '重置中…' : '重新冷启动'}
          </button>
        </div>

        {cloudMessage && <div className="primary-arb-message">{cloudMessage}</div>}
        {cloudStatus?.lastError && (
          <div className="primary-arb-message is-error">最近错误：{cloudStatus.lastError}</div>
        )}
      </section>

      <section className="cxmt-config volume-spike-config volume-spike-config-6">
        <label>
          <span>相对 NAND 阈值（须同时满足下面两个状态才推送）</span>
          <input
            type="number"
            min="10"
            max="500"
            step="1"
            value={nandThresholdPct}
            onChange={(e) => setNandThresholdPct(Math.max(10, Math.min(500, Number(e.target.value) || 90)))}
          />
          <small>默认 90。还须「可进入验证池=是」且「当前矿池=验证池」才会企业微信提醒。</small>
        </label>
        <div>
          <span>数据来源</span>
          <strong>官方 JSON API</strong>
          <small>不刷新网页、不拉网表图，比点「刷新」轻很多</small>
        </div>
        <div>
          <span>NAND 指数缓存</span>
          <strong>{cloudStatus?.nandIndexUsable ? '可用' : '待拉取'}</strong>
          <small>约 5 分钟过期才重拉，不每轮都打</small>
        </div>
        <div>
          <span>元件数缓存</span>
          <strong>{cloudStatus?.burnCacheCount ?? 0} 个 token</strong>
          <small>只在新挂单出现时拉 circuit 详情</small>
        </div>
        <div>
          <span>配置</span>
          <strong>{settingsDirty ? '待保存' : '已同步'}</strong>
          <small>{cloudBusy === 'settings' ? '正在保存…' : '改阈值约 0.8 秒后自动写入'}</small>
        </div>
      </section>

      <section className="volume-spike-grid">
        <div className="volume-spike-panel">
          <div className="volume-spike-panel-head">
            <div>
              <h2>最近事件</h2>
              <p>冷启动、命中推送（最多约 30 条）。须 NAND 低于阈值、可进验证池、当前已在验证池才会发企业微信。</p>
            </div>
          </div>
          <div className="volume-spike-alerts">
            {events.length === 0 ? (
              <div className="volume-spike-empty">暂无事件。请先配置企业微信，再点「开启监控」做冷启动。</div>
            ) : (
              events.map((ev) => (
                <article
                  key={ev.id || `${ev.type}-${ev.at}-${ev.listingKey || ''}`}
                  className={`volume-spike-alert ${ev.type === 'low_nand' ? 'is-multiple' : ''}`}
                >
                  <header>
                    <strong>{ev.message || eventTypeLabel(ev.type)}</strong>
                    <em className={`volume-spike-badge tone-${ev.type === 'low_nand' ? 'bad' : ev.type === 'cold_start' ? 'ok' : 'warm'}`}>
                      {eventTypeLabel(ev.type)}
                    </em>
                  </header>
                  <p>
                    {ev.tokenId ? `Circuit #${ev.tokenId}` : ''}
                    {ev.nandPct != null ? ` · 相对 NAND ${ev.nandPct}%` : ''}
                    {ev.source ? ` · ${ev.source}` : ''}
                  </p>
                  <ul>
                    {ev.listingKey && <li className="mono">{ev.listingKey}</li>}
                    <li>{formatTime(ev.at)}</li>
                  </ul>
                </article>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
